import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  PRODUCT_STATS,
  clampDownloads,
  normalizeBoolean,
  normalizeRating,
  type ProductStats,
  type ProductStatsKey,
  type ProductStatsMap,
} from '@/config/productStats';

interface ProductStatsContextValue {
  stats: ProductStatsMap;
  refreshStats: () => Promise<void>;
}

const ProductStatsContext = createContext<ProductStatsContextValue>({
  stats: PRODUCT_STATS,
  refreshStats: async () => undefined,
});

function normalizeStatsMap(value: unknown): ProductStatsMap {
  const raw = value && typeof value === 'object' ? value as Partial<Record<ProductStatsKey, Partial<ProductStats>>> : {};

  return {
    'defter-kasa': {
      downloads: clampDownloads(raw['defter-kasa']?.downloads ?? PRODUCT_STATS['defter-kasa'].downloads),
      rating: normalizeRating(raw['defter-kasa']?.rating ?? PRODUCT_STATS['defter-kasa'].rating),
      showDownloadsInApps: normalizeBoolean(raw['defter-kasa']?.showDownloadsInApps, PRODUCT_STATS['defter-kasa'].showDownloadsInApps),
    },
    'kozmik-gunluk': {
      downloads: clampDownloads(raw['kozmik-gunluk']?.downloads ?? PRODUCT_STATS['kozmik-gunluk'].downloads),
      rating: normalizeRating(raw['kozmik-gunluk']?.rating ?? PRODUCT_STATS['kozmik-gunluk'].rating),
      showDownloadsInApps: normalizeBoolean(raw['kozmik-gunluk']?.showDownloadsInApps, PRODUCT_STATS['kozmik-gunluk'].showDownloadsInApps),
    },
    'anilar-muzesi': {
      downloads: clampDownloads(raw['anilar-muzesi']?.downloads ?? PRODUCT_STATS['anilar-muzesi'].downloads),
      rating: normalizeRating(raw['anilar-muzesi']?.rating ?? PRODUCT_STATS['anilar-muzesi'].rating),
      showDownloadsInApps: normalizeBoolean(raw['anilar-muzesi']?.showDownloadsInApps, PRODUCT_STATS['anilar-muzesi'].showDownloadsInApps),
    },
  };
}

export function ProductStatsProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<ProductStatsMap>(PRODUCT_STATS);

  const refreshStats = async () => {
    try {
      const response = await fetch('/api/stats', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const data = await response.json().catch(() => null) as { stats?: unknown } | null;
      if (data?.stats) {
        setStats(normalizeStatsMap(data.stats));
      }
    } catch {
      // Keep bundled defaults when the runtime API is unavailable.
    }
  };

  useEffect(() => {
    void refreshStats();
  }, []);

  const value = useMemo(() => ({ stats, refreshStats }), [stats]);

  return (
    <ProductStatsContext.Provider value={value}>
      {children}
    </ProductStatsContext.Provider>
  );
}

export function useProductStats(key: ProductStatsKey): ProductStats {
  return useContext(ProductStatsContext).stats[key];
}

export function useProductStatsMap(): ProductStatsContextValue {
  return useContext(ProductStatsContext);
}
